import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import remove from 'lodash/remove';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { UNAUTHORIZED, FORBIDDEN, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { ApiErrors, Loaders } from 'api/types';
import { i18n } from 'helpers/i18n';
import { TFunction } from 'i18next';
import moment from 'moment';
import { chain } from 'lodash';
import { axiosInstance } from '../axiosInstance';

export type SerialNumber = {
  batch_number: number;
  batch_type: string;
  batch_year: number;
  can_be_deleted: boolean;
  created_at: string;
  documents_count: number;
  external_value?: string;
  has_history: boolean;
  product_id: string;
  qr: string;
  quantity: number;
  site_id: string;
  user_id: string;
  value: string;
};

export type SerialNumberGroup = {
  day: string;
  serial_numbers: SerialNumber[];
};

const dateKey = (item: SerialNumber): string => moment(item.created_at).format('YYYY-MM-DD');

export const formatSerialNumberBatch = (serialNumber: SerialNumber, t: TFunction) => {
  let info = '';

  switch (serialNumber.batch_type) {
    case 'week':
      info = t('products.batch type.week', { week: serialNumber.batch_number, year: serialNumber.batch_year });
      break;
    case 'month':
      info = t('products.batch type.month', { month: serialNumber.batch_number, year: serialNumber.batch_year });
      break;
    case 'three_months':
      info = t(`products.batch type.three months ${serialNumber.batch_number}`, { year: serialNumber.batch_year });
      break;
    case 'four_months':
      info = t(`products.batch type.four months ${serialNumber.batch_number}`, { year: serialNumber.batch_year });
      break;
    default:
      return t('products.batch type.not available');
  }

  return `${t('products.batch')} ${info}`;
};

export type HistorySnapshotProduct = {
  id: string;
  name: string;
  serial_number: string;
};
export type HistorySnapshotComponent = {
  action: string;
  id: string;
  name: string;
  new_product?: HistorySnapshotProduct;
  old_product?: HistorySnapshotProduct;
};

export type HistorySnapshot = {
  author: {
    id: string;
    is_current_user: boolean;
    name: string;
  };
  components: HistorySnapshotComponent[];
  date: string;
  id: string;
  notes?: string;
};

export type SerialNumbersState = {
  apiErrors: ApiErrors;
  configuration?: LastSnapshot;
  groups: SerialNumberGroup[];
  history: HistorySnapshot[];
  list: SerialNumber[];
  loaders: Loaders;
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  selected: {
    [key: string]: boolean;
  };
};

const initialState: SerialNumbersState = {
  apiErrors: {
    loadingError: '',
    savingError: '',
    savingErrorItems: {}
  },
  loaders: {
    isSavingItems: {},
    isSaving: false
  },
  groups: [],
  list: [],
  pagination: {
    size: 60,
    index: 1,
    indexesCount: 0
  },
  history: [],
  configuration: undefined,
  selected: {}
};

type SerialNumberParams = {
  current_order_list?: string | null;
  issuedAfter?: string;
  issuedBefore?: string;
  only_summary?: boolean;
  productId: string;
  queryIndex?: number;
  querySize?: number;
  siteId: string;
};

type CreateSerialNumberParams = {
  count?: number;
  productId: string;
  quantity_per_serial?: number;
  siteId: string;
};

type DeleteSerialNumberParams = {
  serialNumber: string;
};

type FetchSerialNumberResponse = {
  index: number;
  indexesCount: number;
  issuedAfter: string;
  issuedBefore: string;
  serial_numbers: SerialNumber[];
  size: number;
};

type FetchHistoryAndConfigurationParams = {
  productId: string;
  serialNumber: string;
};

type FetchSerialNumberHistory = {
  snapshots: HistorySnapshot[];
};

type SnapshotProductRef = {
  name: string;
  serial_number: string;
};

type SnapshotComponentRef = {
  name: string;
  product: SnapshotProductRef;
};

type SnapshotAuthor = {
  is_current_user: boolean;
  name: string;
};

export type LastSnapshot = {
  author: SnapshotAuthor;
  components: SnapshotComponentRef[];
  date: string;
};

export const fetchSerialNumbers = createAsyncThunk<
  FetchSerialNumberResponse,
  SerialNumberParams,
  { rejectValue: string }
>(
  'serialNumbers/fetchSerialNumbers',
  async (
    { productId, siteId, queryIndex, issuedAfter, issuedBefore, only_summary, current_order_list, querySize },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(`/products/${productId}/sites/${siteId}/serialnumbers`, {
        params: {
          issued_after: issuedAfter,
          index: queryIndex ?? 1,
          issued_before: issuedBefore,
          size: querySize,
          only_summary,
          current_order_list
        }
      });
      const {
        data: { serial_numbers, size, index, indexes_count: indexesCount }
      } = response;

      return {
        size,
        index,
        indexesCount,
        serial_numbers,
        issuedAfter: issuedAfter || '',
        issuedBefore: issuedBefore || ''
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }

      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('products.you cannot generate serial number'));
      }
      if (status === UNPROCESSABLE_ENTITY) {
        return rejectWithValue(i18n.t('products.you cannot fetch serial numbers'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const fetchSerialNumberHistory = createAsyncThunk<
  FetchSerialNumberHistory,
  FetchHistoryAndConfigurationParams,
  { rejectValue: string }
>('serialNumbers/fetchSerialNumberHistory', async ({ productId, serialNumber }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/products/${productId}/serialnumbers/${serialNumber}/history`);
    const { data } = response;
    return data;
  } catch (err) {
    return rejectWithValue(i18n.t('errors.something went wrong')); // TODO: add error management for 401, 403 and 422 status codes.
  }
});

export const fetchSerialNumberConfiguration = createAsyncThunk<
  LastSnapshot,
  FetchHistoryAndConfigurationParams,
  { rejectValue: string }
>('serialNumbers/fetchSerialNumberConfiguration', async ({ productId, serialNumber }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(
      `/products/${productId}/serialnumbers/${serialNumber}/currentconfiguration`
    );
    const { data } = response;
    return data;
  } catch (err) {
    return rejectWithValue(i18n.t('errors.something went wrong')); // TODO: add error management for 401, 403 and 422 status codes.
  }
});

export const createSerialNumber = createAsyncThunk<SerialNumber[], CreateSerialNumberParams, { rejectValue: string }>(
  'serialNumbers/createSerialNumber',
  async ({ productId, siteId, count, quantity_per_serial }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/products/${productId}/site/${siteId}/serialnumber`, null, {
        params: { count: count || 1, quantity_per_serial: quantity_per_serial || 1 }
      });
      const {
        data: { serial_numbers }
      } = response;

      return serial_numbers;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('products.you cannot generate serial number'));
      }
      if (status === UNPROCESSABLE_ENTITY) {
        return rejectWithValue(i18n.t('products.production required for generating serial number'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteSerialNumber = createAsyncThunk<string, string, { rejectValue: string }>(
  'serialNumbers/deleteSerialNumber',
  async (serialNumber, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/serialnumbers/${serialNumber}`);
      return serialNumber;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('products.cannot delete serial number'));
      }
      if (status === UNPROCESSABLE_ENTITY) {
        return rejectWithValue(i18n.t('products.delete serial number error'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

type ExternalParams = {
  externalValue: string;
  serialNumber: string;
};

export const saveExternalSerialNumber = createAsyncThunk<
  SerialNumber,
  ExternalParams,
  { rejectValue: { id: string; msg: string } }
>('serialNumbers/saveExternalSerialNumber', async ({ externalValue, serialNumber }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(updateListLoader({ id: serialNumber, value: true }));
    const response = await axiosInstance.put(`/serialnumbers/${serialNumber}/externalvalue`, {
      external_value: externalValue
    });
    const { data } = response;
    return data;
  } catch (err) {
    /* dispatch(setSaving(scopes.components, false)); */
    if (!err.response) {
      return rejectWithValue({ id: serialNumber, msg: i18n.t('errors.something went wrong') });
    }
    const { status } = err.response;
    if (status === UNAUTHORIZED || status === FORBIDDEN) {
      return rejectWithValue({ id: serialNumber, msg: i18n.t('products.you cannot update serial number') });
    }
    if (status === UNPROCESSABLE_ENTITY) {
      return rejectWithValue({
        id: serialNumber,
        msg: i18n.t('products.production required for generating serial number')
      });
    }
    return rejectWithValue({ id: serialNumber, msg: i18n.t('errors.something went wrong') });
  } finally {
    dispatch(updateListLoader({ id: serialNumber, value: false }));
  }
});

const serialNumbersSlice = createSlice({
  initialState,
  name: 'serialNumbers',
  reducers: {
    updateListLoader: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.savingErrorItems.${id}`, '');
      }
      set(state, `loaders.isSavingItems.${id}`, value);
    },
    resetSerialNumbersList: state => initialState,
    resetSerialNumberHistoryAndConfiguration: state => {
      state.history = [];
      state.configuration = undefined;
    },
    resetSelectedSerialNumber: state => {
      state.selected = {};
    },
    selectSerialNumber: (state, { payload }: PayloadAction<{ key: string; value: boolean }>) => {
      const { key, value } = payload;
      state.selected[key] = value;
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchSerialNumbers.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        /* state.list = uniqBy([...state.list, ...payload.serial_numbers], e => e.value); */
        state.list = [...payload.serial_numbers];
        state.groups = chain(state.list)
          .groupBy(dateKey)
          .map((value, key) => ({ day: key, serial_numbers: value }))
          .value();

        state.pagination = {
          size: payload.size,
          index: payload.index,
          indexesCount: payload.indexesCount
        };
      })
      .addCase(fetchSerialNumbers.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchSerialNumbers.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(fetchSerialNumberHistory.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = '';
        state.history = payload.snapshots;
      })
      .addCase(fetchSerialNumberHistory.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchSerialNumberHistory.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
        state.history = [];
      })
      .addCase(fetchSerialNumberConfiguration.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = '';
        state.configuration = payload;
      })
      .addCase(fetchSerialNumberConfiguration.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchSerialNumberConfiguration.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
        state.configuration = undefined;
      })
      .addCase(createSerialNumber.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        const newList = [...payload, ...state.list];
        state.list = newList;
        state.groups = chain(state.list)
          .groupBy(dateKey)
          .map((value, key) => ({ day: key, serial_numbers: value }))
          .value();
      })
      .addCase(createSerialNumber.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(createSerialNumber.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteSerialNumber.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        const { list } = state;
        const newList = list.filter((obj: SerialNumber) => obj.value !== payload);
        state.list = newList;
        state.groups = chain(newList)
          .groupBy(dateKey)
          .map((value, key) => ({ day: key, serial_numbers: value }))
          .value();
      })
      .addCase(deleteSerialNumber.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(deleteSerialNumber.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(saveExternalSerialNumber.fulfilled, (state, { payload }) => {
        remove(state.list, (e: SerialNumber) => e.value === payload.value);
      })
      .addCase(saveExternalSerialNumber.rejected, (state, { payload }) => {
        set(state, `apiErrors.savingErrorItems.${payload?.id}`, payload?.msg || i18n.t('errors.something went wrong'));
      })
});

const { actions, reducer } = serialNumbersSlice;
export { reducer as serialNumbers };
export const {
  updateListLoader,
  resetSerialNumbersList,
  resetSerialNumberHistoryAndConfiguration,
  resetSelectedSerialNumber,
  selectSerialNumber
} = actions;

export const getSerialNumbers = (state: RootState) => state.serialNumbers.list;
export const getSerialNumberGroups = (state: RootState) => state.serialNumbers.groups;
export const getSerialNumberHistory = (state: RootState) => state.serialNumbers.history;
export const getSerialNumberConfiguration = (state: RootState) => state.serialNumbers.configuration;
export const getSerialNumbersLoaders = (state: RootState) => state.serialNumbers.loaders;
export const getSerialNumbersApiErrors = (state: RootState) => state.serialNumbers.apiErrors;
export const getSerialNumbersPagination = (state: RootState) => state.serialNumbers.pagination;
export const getSelectedSerialNumbers = (state: RootState) => state.serialNumbers.selected;
